import BreadCrumb from "components/Common/BreadCrumb";
import TooltipCustom from "components/Common/TooltipCustom";
import { formatScientificNumber, formatStringDotDotDot, formatTokenNumber } from "helpers/format";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { Card, Col, Container, Input, Row } from "reactstrap";
import { createSelector } from "reselect";
import { StringParam, useQueryParams, withDefault } from "use-query-params";
import { getOptionPools as onGetOptionPools } from "../../store/thunks";
import SearchPool from "./SearchPool";
import SolanaTokenChart from "./SolanaChart";
import TransactionList from "./Transactions";
import { useRealtimeTrade } from "components/Hooks/ws/useRealtimeTrade";
import { Link } from "react-router-dom";
import CopyWrapper from "components/Common/CopyWrapper";
import LineChart from "./LineChart";
import chartApi from "api/chart/chartApi";


const Trade = () => {

  const { t } = useTranslation();

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => {
    return state.Pool;
  };

  const PoolProperties = createSelector(selectLayoutState, (state) => ({
    optionPools: state.optionPools || [],
    isOptionPoolSuccess: state.isOptionPoolSuccess,
    isOptionPoolLoading: state.isOptionPoolLoading,
    error: state.error,
  }));

  // Inside your component
  const { optionPools } = useSelector(PoolProperties);

  useEffect(() => {
    dispatch(onGetOptionPools({}));
  }, [dispatch]);


  const [poolDetail, setPoolDetail] = useState<any>(null);

  const [isOpenSearchPool, setIsOpenSearchPool] = useState<boolean>(false);

  const [chartData, setChartData] = useState<any[]>([]);

  const [query, setQuery]: any = useQueryParams({
    token: withDefault(StringParam, ""),
  });

  useEffect(() => {
    document.title = `${t("Trade")} - ${t("Trade")}`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const getList = async () => {
      try {
        if (optionPools?.length === 0) {
          return;
        }
        if (query?.token) {
          const _poolByToken = (optionPools || [])?.find((_p: any) => String(_p?.pairAddress) === String(query?.token));
          setPoolDetail((_prev: any) => _poolByToken);
        } else {
          setQuery({ token: optionPools?.[0]?.pairAddress }, "push");
        }
      } catch (error) {
        console.error(error);
      }
    };
    getList();
  }, [JSON.stringify(optionPools), query?.token]);


  const onCloseSearchPool = () => {
    setIsOpenSearchPool((_prev) => false);
  };

  const onOpenSearchPool = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    setIsOpenSearchPool((_prev) => true);
  };

  const handleChangePool = (__pool: any) => {
    setQuery({ token: __pool?.pairAddress }, "push");
    onCloseSearchPool();
    // setPoolDetail((_prev: any) => __pool);
  };

  const getChartData = useCallback((data: any, typeShowPriceColumn: string = 'NATIVE') => {

    const _data = data?.filter((item: any) => ['sell', 'buy']?.includes(item?.transactionType)).map((item: any) => {
      return {
        time: item?.timestamp,
        value: typeShowPriceColumn === 'USD' ? item?.baseTokenPriceUsd : item?.baseQuotePrice,
      };
    });
    setChartData(_data);
  }, []);


  const [isHaveDataChart, setIsHaveDataChart] = useState<boolean>(true);

  useEffect(() => {
    if (query?.token) {
      const getChartData = async () => {
        if (query?.token) {
          const response: any = await chartApi.getPairOhlcv({
            address: query?.token,
            timePeriod: '1',
            timeFrame: '1min',
            fromDate: '2025-06-19T17:00:00.000Z',
            toDate: '2025-07-01T02:16:17.434Z',
            currency: "usd",
          });
          setIsHaveDataChart(response?.data?.result?.length > 0);
        }
      };
      getChartData();
    }
  }, [query?.token]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Trade")} pageTitle={t("Home")} />
          <div>
            <Card
              id="customerList"
              style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              className="px-0"
            >
              <div>
                <Row className="align-items-center z-[1001] mt-2 px-3 px-lg-3">
                  <Col
                    className="my-1 order-1 order-lg-0"
                    sm={5}
                    md={6}
                    lg={3}
                    xl={3}
                    xxl={2}
                  >
                    <div className="flex items-center space-x-3 cursor-pointer">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <img src={poolDetail?.tokens?.[0]?.tokenLogo || poolDetail?.tokens?.[1]?.tokenLogo || poolDetail?.exchangeLogo} alt="Token Icon" className="w-full h-full object-contain rounded-full" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-90">{poolDetail?.exchangeName || '-'}</div>
                        <div className="fs-14 text-gray-600">
                          {poolDetail?.pairLabel ? <span><strong className="font-semibold me-1 fs-16">{String(poolDetail?.pairLabel)?.split('/')[0]}</strong>/<span className="ms-1 fs-12 text-muted">{String(poolDetail?.pairLabel)?.split('/')[1]}</span></span> : ('-')}
                        </div>
                        <div className="text-xs text-gray-500">
                          <Link to={`https://solscan.io/account/${poolDetail?.pairAddress}`} target="_blank" className="d-inline-block">
                            <CopyWrapper
                              isShow={true}
                              contentCopy={poolDetail?.pairAddress}
                            >
                              {formatStringDotDotDot(poolDetail?.pairAddress, 8, 5)}
                            </CopyWrapper>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col
                    sm={12}
                    md={12}
                    lg={5}
                    xl={6}
                    xxl={7}
                    className="order-3 order-lg-0"
                  >
                    <div>
                      <div className="app-search px-0" onClick={onOpenSearchPool}>
                        <div>
                          <div className="position-relative layer-on-input">
                            <Input type="text" className="form-control !bg-[#f3f4f6]" placeholder={`${t('Search pair by symbol, name, contract or token')}...`}
                              id="search-options"
                              autoComplete="off"
                              autoFocus={false}
                              value=""
                            />
                            <span className="mdi mdi-magnify search-widget-icon"></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col
                    sm={7}
                    md={6}
                    lg={4}
                    xl={3}
                    xxl={3}
                    className="order-2 order-lg-0"
                  >
                    <div className="flex items-center justify-end">
                      {/* Price Section */}
                      <div className="flex items-center space-x-2 p-3 py-2 bg-gray-100 rounded-lg" >
                        <span className="text-3xl font-semibold text-[#4387f9]">
                          {formatScientificNumber(poolDetail?.usdPrice, 4, '$ ')}
                        </span>
                      </div>

                      {/* Price Change Section */}
                      <div className="text-end pe-2 ps-4">
                        <div className={`text-sm font-medium ${poolDetail?.usdPrice24hrPercentChange < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {Number(poolDetail?.usdPrice24hrPercentChange) < 0 ? <i className="mdi mdi-trending-down align-middle"></i> : <i className="mdi mdi-trending-up align-middle"></i>} {formatTokenNumber(poolDetail?.usdPrice24hrPercentChange, 2, '')}% <span className="text-gray-400 ms-1">24h</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <TooltipCustom
                            title={poolDetail?.liquidityUsd}
                            className="d-inline-block vertical-align-middle"
                            style={{ transform: 'translateY(2px)' }}
                          >
                            Liq. {formatTokenNumber(poolDetail?.liquidityUsd, 2, '$')}
                          </TooltipCustom>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{
                borderBottom: 'var(--vz-card-border-width) solid var(--vz-card-border-color)',
                margin: '5px auto',
                opacity: '0.5'
              }} className="w-100"></div>

              <div>
                <div className={isHaveDataChart ? '' : 'd-none'}>
                  <SolanaTokenChart
                    pairAddress={query?.token || ''}
                  />
                </div>
                <div className={isHaveDataChart ? 'd-none' : ''}>
                  <LineChart
                    data={chartData}
                    tokenSymbols={{
                      symbol: [poolDetail?.tokens?.[0]?.tokenSymbol || '', poolDetail?.tokens?.[1]?.tokenSymbol || ''],
                      decimal: [poolDetail?.tokens?.[0]?.tokenDecimals || '', poolDetail?.tokens?.[1]?.tokenDecimals || ''],
                    }}
                  />
                </div>

              </div>
            </Card>

            <div
              id="customerList"
              style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              className="px-0"
            >
              <TransactionList pairAddress={query?.token || ''} tokenSymbols={{
                symbol: [poolDetail?.tokens?.[0]?.tokenSymbol || '', poolDetail?.tokens?.[1]?.tokenSymbol || ''],
                decimal: [poolDetail?.tokens?.[0]?.tokenDecimals || '', poolDetail?.tokens?.[1]?.tokenDecimals || ''],
              }}
                getChartData={getChartData}
              />
            </div>

          </div>
          <SearchPool isOpen={isOpenSearchPool} onClose={onCloseSearchPool} changePool={handleChangePool} />
        </Container>
        <ToastContainer autoClose={2000} limit={1} />
      </div>
    </React.Fragment>
  );
};

export default Trade;

